/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'LuTheme:viewedProducts';
  /** @static @constant {number} The maximum number of products to store */

  /**
   * Adds a product to the recently viewed products list.
   * Removes existing entry if present, adds to front, and limits total count
   * @param {string} productId - The ID of the product to add
   * @param {number} [storeLimit=50] - Maximum number of products to store
   */
  static addProduct(productId, storeLimit = 50) {
    try {
      const allProducts = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      let viewedProducts = allProducts.filter((/** @type {string} */ id) => id !== productId);
      viewedProducts.unshift(productId);
      viewedProducts = viewedProducts.slice(0, storeLimit);
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // localStorage unavailable (private browsing, storage full, etc.)
    }
  }

  /**
   * Clears all recently viewed products from localStorage
   */
  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage with pagination support.
   * @param {number} limit - Maximum number of products to return (default: 6)
   * @param {number} offset - Starting position (1-based, default: 1).
   *                         offset=1 returns first products, offset=21 returns products from position 21
   * @returns {string[]} The list of viewed products.
   */
  static getProducts(limit = 6, offset = 1) {
    try {
      const products = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      const startIndex = Math.max(0, offset - 1);
      return products.slice(startIndex, startIndex + limit);
    } catch {
      return [];
    }
  }

  /**
   * Gets the total count of products stored in localStorage.
   * @returns {number} Total number of products.
   */
  static getTotalCount() {
    try {
      const products = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return products.length;
    } catch {
      return 0;
    }
  }
}

/**
 * Manages recently searched keywords in localStorage
 */
export class RecentlySearched {
  static #STORAGE_KEY = 'LuTheme:recentlySearched';

  /**
   * Adds a keyword to the recently searched list
   * Removes existing entry if present, adds to front, and limits total count
   * @param {string} key - The keyword to add
   * @param {number} [storeLimit=12] - Maximum number of keywords to store
   */
  static addKeyWords(key, storeLimit = 12) {
    if (!key) return;
    try {
      let keyWords = this.getKeyWords(storeLimit);
      keyWords = keyWords.filter((/** @type {string} */ id) => id !== key);
      keyWords.unshift(key);
      keyWords = keyWords.slice(0, storeLimit);
      if (keyWords.length > 0) {
        localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(keyWords));
      }
    } catch {
      // localStorage unavailable (private browsing, storage full, etc.)
    }
  }

  /**
   * Clears all recently searched keywords from localStorage
   */
  static clearKeyWords() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
  }

  /**
   * Retrieves recently searched keywords from localStorage
   * @param {number} [limit=5] - Maximum number of keywords to return
   * @returns {string[]} Array of recently searched keywords
   */
  static getKeyWords(limit = 5) {
    try {
      const keyWords = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return keyWords.slice(0, limit);
    } catch {
      return [];
    }
  }
}